import { entryProblem, normalizeEntry, pendingEntry, sameAddress, type AnchorReason, type MeetingEntry, type RouteAnchor } from '../../../shared/routeAnchor';
import { retainedMeetingPoints } from '../data/dayRoutes';
import { h } from '../ui/dom';
import { field } from '../ui/components';

/** A Route-search pick the form must finish (D120 amendment): what Google's components gave, and why. */
export interface MeetingDraft { entry: Partial<MeetingEntry>; message: string }

/** What the form's status line says and whether Resolve is held (CAY-115). `line: null` is the
 *  form's own default (the anchor's reason, or the entry hint). `lockFor`: while the fields hold
 *  this address (label aside: it is display text, a new label would repeat the same lookup), the
 *  submit is disabled, so a second tap cannot queue a second Census lookup for the same building. */
export interface FormStatus { line: string | null; tone?: 'warn'; lockFor: MeetingEntry | null }
export interface MeetingForm { root: HTMLFormElement; refresh: (status: FormStatus) => void }

const HINT = 'Enter a building address. Unit is display text; the map locates the building.';
const defaultLine = (anchor: RouteAnchor | undefined, draft?: MeetingDraft): { line: string; tone?: 'warn' } =>
  draft ? { line: draft.message }
  : anchor?.kind === 'unresolved' ? { line: reasonText(anchor.reason), tone: reasonTone(anchor.reason) }
  : { line: HINT };

/** Independent text only: no settings-derived defaults. A `draft` (an incomplete Route-search pick)
 *  pre-fills the fields instead of the stored entry; nothing is saved until the rep resolves it. */
export const meetingPointForm = (anchor: RouteAnchor | undefined, save: (anchor: RouteAnchor) => Promise<void>, draft?: MeetingDraft, status?: FormStatus): MeetingForm => {
  const initial = draft?.entry ?? (anchor && (anchor.kind === 'census_entry' || anchor.kind === 'unresolved') ? anchor.entry : undefined);
  const fields = {} as Record<keyof MeetingEntry, HTMLInputElement>;
  const message = h('p', { role: 'status' });
  const form = h('form', { class: 'stack', novalidate: true }, message);
  const limits: Record<keyof MeetingEntry, number> = { street: 200, unit: 40, city: 100, state: 2, zip: 5, label: 300 };
  for (const key of ['street', 'unit', 'city', 'state', 'zip', 'label'] as const) {
    const input = h('input', { class: 'input', name: key, value: initial?.[key] ?? (key === 'state' ? 'CA' : ''), maxlength: limits[key], required: !['unit','label'].includes(key), ...(key === 'zip' ? { pattern: '[0-9]{5}', inputmode: 'numeric' } : {}), ...(key === 'state' ? { pattern: '[A-Za-z]{2}' } : {}) });
    fields[key] = input;
    form.append(field(key === 'label' ? 'Label (optional)' : key === 'street' ? 'Street (building address)' : key[0]!.toUpperCase() + key.slice(1), input));
  }
  const reuse = h('select', { class: 'input select', 'aria-label': 'Reuse meeting point' }, h('option', { value: '' }, 'Reuse meeting point…'));
  const choices = retainedMeetingPoints();
  choices.forEach((p, i) => reuse.append(h('option', { value: String(i) }, `${p.entry.label || p.entry.street} · ${p.day ?? ''}`)));
  if (choices.length) form.append(field('Reuse meeting point', reuse));
  const submit = h('button', { type: 'submit', class: 'btn btn--primary' }, 'Resolve meeting point');
  form.append(submit);

  const current = () => normalizeEntry(Object.fromEntries(Object.entries(fields).map(([key, input]) => [key, input.value])) as unknown as MeetingEntry);
  let lockFor: MeetingEntry | null = null;
  let busy = false;
  const locked = () => !!lockFor && sameAddress(current(), lockFor);
  const paintSubmit = () => { submit.disabled = busy || locked(); };
  const say = (line: string, tone?: 'warn') => {
    message.textContent = line;
    message.classList.toggle('text-warn', tone === 'warn');
  };
  const refresh = (next: FormStatus) => {
    const fallback = defaultLine(anchor, draft);
    if (next.line === null) say(fallback.line, fallback.tone);
    else say(next.line, next.tone);
    lockFor = next.lockFor;
    paintSubmit();
  };
  refresh(status ?? { line: null, lockFor: null });
  form.addEventListener('input', paintSubmit);

  reuse.addEventListener('change', () => {
    if (reuse.value === '') return;
    const chosen = choices[Number(reuse.value)]!;
    for (const key of Object.keys(fields) as (keyof MeetingEntry)[]) fields[key].value = chosen.entry[key];
    say('Review this address, then Resolve meeting point. This day gets a new lookup.');
    paintSubmit();
  });
  form.addEventListener('submit', ev => {
    ev.preventDefault();
    if (busy || locked()) return; // Enter in a field submits even with the button disabled
    const entry = current();
    for (const key of Object.keys(fields) as (keyof MeetingEntry)[]) fields[key].value = entry[key];
    if (!form.reportValidity()) return;
    const problem = entryProblem(entry);
    if (problem) { say(reasonText(problem), reasonTone(problem)); return; }
    busy = true;
    paintSubmit();
    void save(pendingEntry(entry)).catch(() => { say('Could not queue the meeting point. Please retry.', 'warn'); }).finally(() => { busy = false; paintSubmit(); });
  });
  return { root: form, refresh };
};

/** The outcomes that mean the lookup failed read in `--warn`; pending, drafts and entry hints stay `--text`. */
const WARN_REASONS: ReadonlySet<string> = new Set<AnchorReason>(['no_match', 'needs_review', 'timeout', 'unavailable', 'bad_response']);
export const reasonTone = (reason: string): 'warn' | undefined => (WARN_REASONS.has(reason) ? 'warn' : undefined);
export const reasonText = (reason: string): string => ({
  pending: 'Meeting point pending. Connect to resolve; saved stops remain available.',
  unit_in_street: 'Move the suite, apartment or unit from Street into Unit, then retry.',
  invalid_street: 'Enter a building number and street in Street, then retry.',
  timeout: 'Address lookup timed out. Check the address and retry.',
  no_match: 'No matching building found. Check the address and retry.',
  needs_review: 'Could not verify a single matching building. Check street, state and ZIP, then retry.',
  unavailable: 'Address lookup is unavailable. Retry when connected.',
  bad_response: 'Address lookup could not be read. Please retry.',
}[reason] ?? 'Meeting point needs entry. Enter an independent address or reuse a previous entry.');
